
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/components/ui/use-toast";
import { AlertCircle, RefreshCw, Save, Database, FolderPlus, Loader2 } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { StorageService } from '@/services/StorageService';

const AdminStorageSettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingBucket, setIsCreatingBucket] = useState(false);
  const [storageProvider, setStorageProvider] = useState('supabase');
  const [baseUrl, setBaseUrl] = useState('');
  const [newBucketName, setNewBucketName] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [buckets, setBuckets] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
    fetchBuckets();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('*')
        .in('setting_name', ['storage.provider', 'storage.base_url']);
      
      if (error) throw error;
      
      const providerSetting = data.find(item => item.setting_name === 'storage.provider');
      const baseUrlSetting = data.find(item => item.setting_name === 'storage.base_url');
      
      if (providerSetting) {
        setStorageProvider(providerSetting.setting_value);
      }
      
      if (baseUrlSetting) {
        setBaseUrl(baseUrlSetting.setting_value);
      }
      
    } catch (error) {
      console.error('Error fetching storage settings:', error);
      setError('Failed to load storage settings');
      toast({
        title: 'Error',
        description: 'Failed to load storage settings',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBuckets = async () => {
    try {
      const { data, error } = await supabase.storage.listBuckets();
      
      if (error) throw error;
      
      setBuckets(data || []);
    } catch (error) {
      console.error('Error fetching buckets:', error);
      toast({
        title: 'Error',
        description: 'Failed to load storage buckets',
        variant: 'destructive'
      });
    }
  };

  const saveSettings = async () => {
    setIsSaving(true);
    setError(null);
    
    try {
      // Check if settings already exist
      const { data: existingSettings, error: fetchError } = await supabase
        .from('admin_settings')
        .select('id, setting_name')
        .in('setting_name', ['storage.provider', 'storage.base_url']);
      
      if (fetchError) throw fetchError;
      
      const providerSetting = existingSettings.find(s => s.setting_name === 'storage.provider');
      const baseUrlSetting = existingSettings.find(s => s.setting_name === 'storage.base_url');
      
      // Prepare updates or inserts
      const updates = [];
      
      if (providerSetting) {
        updates.push(
          supabase
            .from('admin_settings')
            .update({ setting_value: storageProvider })
            .eq('id', providerSetting.id)
        );
      } else {
        updates.push(
          supabase
            .from('admin_settings')
            .insert({
              setting_name: 'storage.provider',
              setting_value: storageProvider,
              description: 'Storage provider for file uploads'
            })
        );
      }
      
      if (baseUrlSetting) {
        updates.push(
          supabase
            .from('admin_settings')
            .update({ setting_value: baseUrl })
            .eq('id', baseUrlSetting.id)
        );
      } else {
        updates.push(
          supabase
            .from('admin_settings')
            .insert({
              setting_name: 'storage.base_url',
              setting_value: baseUrl,
              description: 'Base URL for storage access'
            })
        );
      }
      
      // Execute all updates
      await Promise.all(updates.map(query => query));
      
      toast({
        title: 'Success',
        description: 'Storage settings saved successfully',
      });
    } catch (error) {
      console.error('Error saving storage settings:', error);
      setError('Failed to save settings');
      toast({
        title: 'Error',
        description: 'Failed to save storage settings',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateBucket = async () => {
    if (!newBucketName) {
      toast({
        title: 'Error',
        description: 'Please enter a bucket name',
        variant: 'destructive'
      });
      return;
    }
    
    setIsCreatingBucket(true);
    
    try {
      // Check if bucket exists
      const exists = await StorageService.checkBucketExists(newBucketName);
      
      if (exists) {
        toast({
          title: 'Error',
          description: `Bucket "${newBucketName}" already exists`,
          variant: 'destructive'
        });
        setIsCreatingBucket(false);
        return;
      }
      
      // Create bucket
      const success = await StorageService.createBucket(newBucketName, isPublic);
      
      if (success) {
        toast({
          title: 'Success',
          description: `Bucket "${newBucketName}" created successfully`,
        });
        setNewBucketName('');
        fetchBuckets();
      } else {
        toast({
          title: 'Error',
          description: 'Failed to create bucket',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error creating bucket:', error);
      toast({
        title: 'Error',
        description: 'Failed to create bucket',
        variant: 'destructive'
      });
    } finally {
      setIsCreatingBucket(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Storage Configuration</CardTitle>
          <CardDescription>
            Configure how files are stored and accessed
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Storage Provider</h3>
              
              <RadioGroup 
                value={storageProvider}
                onValueChange={setStorageProvider}
                className="space-y-3"
              >
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="supabase" id="supabase" />
                  <div className="grid gap-1">
                    <Label htmlFor="supabase" className="font-semibold">Supabase Storage</Label>
                    <p className="text-sm text-gray-500">
                      Use Supabase Storage buckets for file uploads
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="local" id="local" />
                  <div className="grid gap-1">
                    <Label htmlFor="local" className="font-semibold">Local File System</Label>
                    <p className="text-sm text-gray-500">
                      Save files to the local public folder structure
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-2">
                  <RadioGroupItem value="external_url" id="external_url" />
                  <div className="grid gap-1">
                    <Label htmlFor="external_url" className="font-semibold">External URL</Label>
                    <p className="text-sm text-gray-500">
                      Use external URLs directly for images and files
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="base_url">Base URL</Label>
              <Input
                id="base_url"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://example.com/files"
              />
              <p className="text-xs text-gray-500">
                Base URL for accessing files (leave empty for default)
              </p>
            </div>
            
            <Button onClick={saveSettings} disabled={isSaving}>
              {isSaving ? 'Saving...' : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
          
          <div className="border-t my-6"></div>
          
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Storage Buckets</h3>
            <p className="text-sm text-gray-500">
              Create and manage storage buckets
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bucket_name">New Bucket Name</Label>
                <Input
                  id="bucket_name"
                  value={newBucketName}
                  onChange={(e) => setNewBucketName(e.target.value)}
                  placeholder="my-bucket"
                />
              </div>
              <div className="space-y-2">
                <Label>Access</Label>
                <RadioGroup 
                  value={isPublic ? "public" : "private"}
                  onValueChange={(v) => setIsPublic(v === "public")}
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="public" id="public" />
                    <Label htmlFor="public">Public</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="private" id="private" />
                    <Label htmlFor="private">Private</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>
            
            <Button onClick={handleCreateBucket} disabled={isCreatingBucket}>
              {isCreatingBucket ? 'Creating...' : (
                <>
                  <FolderPlus className="mr-2 h-4 w-4" />
                  Create Bucket
                </>
              )}
            </Button>
            
            <div className="border-t my-2"></div>
            
            <div>
              <h4 className="font-medium mb-2">Existing Buckets</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {buckets.length > 0 ? (
                  buckets.map(bucket => (
                    <div key={bucket.id} className="p-3 border rounded-md flex items-center">
                      <Database className="mr-2 h-4 w-4 text-blue-500" />
                      <div>
                        <p className="font-medium">{bucket.name}</p>
                        <p className="text-xs text-gray-500">
                          {bucket.public ? 'Public' : 'Private'}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 col-span-3">No buckets found</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminStorageSettings;
